import { motion } from 'framer-motion';
import SEO from '@/components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const Section = ({ title, children }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={fadeInUp}
    className="mb-10"
  >
    <h2 className="font-playfair text-2xl text-navy mb-4">{title}</h2>
    <div className="text-mid-grey leading-relaxed space-y-3">{children}</div>
  </motion.div>
);

const PrivacyPolicyPage = () => {
  return (
    <div className="pt-20" data-testid="privacy-policy-page">
      <SEO
        title="Privacy Policy"
        description="How H&G (Horizon & Greenprint) collects, uses, and protects your personal data, in accordance with India's DPDP Act, 2023 and the EU General Data Protection Regulation (GDPR)."
        path="/privacy-policy"
      />

      {/* Hero */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-white via-light-grey to-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="font-playfair text-4xl md:text-5xl text-navy mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-mid-grey">
              Effective date: August 10, 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">

          <Section title="1. Who We Are">
            <p>
              H&G (Horizon &amp; Greenprint) (&ldquo;H&amp;G&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is
              responsible for the personal data you share with us through hng.world (the &ldquo;Website&rdquo;).
              Depending on where you are located, we act as your &ldquo;Data Fiduciary&rdquo; under India&rsquo;s
              Digital Personal Data Protection Act, 2023 (&ldquo;DPDP Act&rdquo;), or your &ldquo;Data
              Controller&rdquo; under the EU/UK General Data Protection Regulation (&ldquo;GDPR&rdquo;). This
              policy explains what personal data we collect, why, how we use and protect it, and the rights
              available to you under whichever of these laws applies to you.
            </p>
            <p>
              H&amp;G maintains offices in Amsterdam (Netherlands), Nashik (India), and Pune (India). Our
              Amsterdam office is our point of contact for visitors in the European Economic Area (EEA) and the
              United Kingdom.
            </p>
          </Section>

          <Section title="2. Personal Data We Collect">
            <p>When you submit an enquiry through our Contact form, we collect:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Company name</li>
              <li>The contents of your message</li>
              <li>Technical data needed to prevent spam and abuse (your IP address, processed by Cloudflare Turnstile at the point of submission)</li>
            </ul>
            <p>
              We do not use cookies or trackers to profile visitors on this Website, and we do not collect
              personal data through any means other than the Contact form described above.
            </p>
          </Section>

          <Section title="3. Why We Collect It (Purpose)">
            <p>We process your personal data solely to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Respond to your enquiry and correspond with you about it</li>
              <li>Understand your requirements and, where relevant, provide a proposal or quotation</li>
              <li>Maintain a record of business correspondence for legitimate operational and legal purposes</li>
              <li>Detect and prevent spam, fraud, or abuse of the Contact form</li>
            </ul>
            <p>
              We do not use your data for unrelated marketing without separately asking for your consent, and we
              collect no more data than is necessary for these purposes (GDPR&rsquo;s principle of data
              minimisation).
            </p>
          </Section>

          <Section title="4. Legal Basis for Processing">
            <p>
              We process your personal data on the basis of your explicit, informed, and freely given consent,
              provided by checking the consent box before submitting the Contact form. For visitors in the
              EEA/UK, this corresponds to consent under Article 6(1)(a) GDPR. You may withdraw your consent at
              any time (see Section 8), though this will not affect the lawfulness of processing carried out
              before withdrawal, and may limit our ability to respond to an enquiry already in progress.
            </p>
          </Section>

          <Section title="5. How We Share Your Data">
            <p>
              We do not sell your personal data. It is shared only with the following processors, strictly to
              operate the Website and deliver the service you requested:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong className="text-navy">Cloudflare, Inc.</strong> &mdash; operates the Turnstile
                verification challenge used to distinguish human submissions from automated bots. Cloudflare
                processes your IP address and browser signals for this purpose.
              </li>
              <li>
                <strong className="text-navy">Google LLC (Gmail/Google Workspace)</strong> &mdash; used as our
                email delivery provider to transmit your enquiry to our team.
              </li>
              <li>
                <strong className="text-navy">Our hosting provider</strong> &mdash; stores the Website and
                processes form submissions on the server that delivers this site to you.
              </li>
            </ul>
            <p>
              Cloudflare and Google may process data on servers located outside your country, including outside
              India and outside the EEA/UK. Where personal data is transferred internationally, we rely on the
              safeguards these processors publish for such transfers, including Standard Contractual Clauses
              (SCCs) recognised under GDPR and other legally recognised transfer mechanisms. We may also disclose
              personal data where required by law or a valid order of a court or government authority.
            </p>
          </Section>

          <Section title="6. Data Retention">
            <p>
              We retain enquiry records for as long as reasonably necessary to fulfil the purpose described in
              Section 3, or as required to comply with our legal, accounting, or reporting obligations,
              whichever is longer. When no longer needed, we delete or anonymize the data.
            </p>
          </Section>

          <Section title="7. How We Protect Your Data">
            <p>
              Form submissions are transmitted over an encrypted (HTTPS) connection. Access to enquiry records
              is restricted to H&amp;G personnel who need it to respond to you. We take reasonable technical and
              organizational measures to guard against unauthorized access, alteration, disclosure, or
              destruction of your personal data.
            </p>
          </Section>

          <Section title="8. Your Rights">
            <p className="text-navy font-medium">If the DPDP Act applies to you (India), you have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Obtain a summary of the personal data we hold about you and how it has been processed</li>
              <li>Request correction, completion, or updating of your personal data</li>
              <li>Request erasure of your personal data, unless we are required to retain it by law</li>
              <li>Withdraw your consent at any time, with the same ease with which it was given</li>
              <li>Nominate another individual to exercise these rights on your behalf in the event of your death or incapacity</li>
              <li>Register a grievance regarding our handling of your personal data, and if unresolved, approach the Data Protection Board of India</li>
            </ul>

            <p className="text-navy font-medium mt-6">If the GDPR applies to you (EEA/UK), you have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access the personal data we hold about you</li>
              <li>Rectify inaccurate or incomplete personal data</li>
              <li>Erase your personal data (&ldquo;right to be forgotten&rdquo;), unless we are required to retain it by law</li>
              <li>Restrict or object to our processing of your personal data</li>
              <li>Receive your personal data in a structured, commonly used format (data portability)</li>
              <li>Withdraw your consent at any time, without affecting processing already carried out</li>
              <li>Lodge a complaint with your local data protection supervisory authority &mdash; for example, the Dutch Data Protection Authority (Autoriteit Persoonsgegevens), given our Amsterdam office</li>
            </ul>

            <p>
              To exercise any of these rights, email us at{' '}
              <a href="mailto:contact@hng.world" className="text-teal hover:underline">contact@hng.world</a>{' '}
              with your request. We will respond within a reasonable time and in accordance with the applicable
              law.
            </p>
          </Section>

          <Section title="9. Grievance Officer / EU Contact Point">
            <p>
              If you have a concern about how your personal data has been processed, please contact our
              Grievance Officer:
            </p>
            <p className="text-navy font-medium">
              Email: <a href="mailto:contact@hng.world" className="text-teal hover:underline">contact@hng.world</a>
            </p>
            <p>
              For visitors in the EEA/UK, our Amsterdam office (De Entree 232, 1101EE, Amsterdam, Netherlands)
              serves as the contact point for data protection matters. We will acknowledge and address
              grievances within a reasonable time.
            </p>
          </Section>

          <Section title="10. Children's Data">
            <p>
              This Website and our services are intended for business use by adults. We do not knowingly
              collect personal data from children. If you believe a child has provided us with personal data,
              please contact us so we can delete it.
            </p>
          </Section>

          <Section title="11. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal
              requirements. The &ldquo;Effective date&rdquo; above indicates when this policy was last revised.
              Material changes will be reflected on this page.
            </p>
          </Section>

          <Section title="12. Contact Us">
            <p>
              For any questions about this Privacy Policy or our data practices, write to us at{' '}
              <a href="mailto:contact@hng.world" className="text-teal hover:underline">contact@hng.world</a>.
            </p>
          </Section>

        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
